import { useMemo, useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Save, CheckCircle2, Plus, Trash2, Loader2 } from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { useOpportunities } from "@/hooks/useOpportunities";
import {
  adminGetOpportunity,
  adminPatchOpportunity,
  adminPublishOpportunity,
} from "@/services/adminService";
import { ADMIN } from "@/constants/testIds";

const Field = ({ label, hint, required, children, span = 1 }) => (
  <div className={span === 2 ? "sm:col-span-2" : ""}>
    <label className="mb-1.5 flex items-center justify-between text-xs font-medium text-ink-700">
      <span>
        {label}
        {required && <span className="ms-1 text-red-600">*</span>}
      </span>
      {hint && <span className="text-[11px] font-normal text-ink-500">{hint}</span>}
    </label>
    {children}
  </div>
);

const baseInput =
  "block w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-ink-900 shadow-sm outline-none transition-colors placeholder:text-ink-500/70 focus:border-intel-500 focus:ring-2 focus:ring-intel-500/20";

const NotFound = ({ id }) => (
  <AdminLayout>
    <div
      data-testid={ADMIN.editorNotFound}
      className="rounded-xl border border-dashed border-surface-200 bg-white px-6 py-16 text-center"
    >
      <h2 className="text-lg font-semibold text-ink-900">الفرصة غير موجودة</h2>
      <p className="mt-2 text-sm text-ink-500">
        لم نعثر على فرصة بالمعرِّف <span className="font-mono">{id}</span>.
      </p>
      <Link
        to="/admin/review"
        className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-intel-800 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-intel-700"
      >
        <ArrowRight className="h-4 w-4 -scale-x-100" strokeWidth={1.75} />
        العودة إلى قائمة المراجعة
      </Link>
    </div>
  </AdminLayout>
);

export default function OpportunityEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { countries, industries, refresh: refreshPublic } = useOpportunities();

  // Admin editor loads from /api/admin/opportunities/{id} so pending/rejected
  // opps are editable (the public detail endpoint filters to `published`).
  const [base, setBase] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setIsLoading(true);
    setNotFound(false);
    adminGetOpportunity(id)
      .then((row) => { if (!cancelled) setBase(row); })
      .catch((e) => {
        if (cancelled) return;
        if (String(e).includes(" 404 ")) setNotFound(true);
        else toast.error("تعذّر تحميل الفرصة", { description: String(e) });
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [id]);
  const opportunityTypes = useMemo(
    () => [
      { slug: "acquisition",  name: "فرصة استحواذ" },
      { slug: "partnership",  name: "فرصة شراكة" },
      { slug: "investment",   name: "فرصة استثمار" },
      { slug: "tender",       name: "فرصة مناقصة" },
      { slug: "project",      name: "فرصة مشروع" },
      { slug: "exit",         name: "فرصة مخارجة" },
      { slug: "emerging",     name: "القطاع الصاعد" },
      { slug: "signal",       name: "إشارة السوق المبكرة" },
    ],
    [],
  );

  const [form, setForm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  useEffect(() => {
    if (!base) return;
    setForm({
      title: base.title ?? "",
      type: base.type ?? "",
      country: base.country ?? "",
      industry: base.industry ?? "",
      score: base.score ?? 70,
      summary: base.summary ?? "",
      executiveSummary: base.executiveSummary ?? "",
      highlights: base.highlights?.length ? [...base.highlights] : [""],
      keyIndicators:
        base.keyIndicators?.length ? [...base.keyIndicators] : [{ label: "", value: "" }],
      suggestedSteps:
        base.suggestedSteps?.length ? [...base.suggestedSteps] : [""],
      tags: base.tags?.length ? [...base.tags] : [],
    });
  }, [base]);

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="rounded-xl border border-dashed border-surface-200 bg-white px-6 py-16 text-center">
          <p className="text-sm text-ink-500">جاري تحميل الفرصة…</p>
        </div>
      </AdminLayout>
    );
  }

  if (notFound || !base) return <NotFound id={id} />;

  if (!form) {
    return (
      <AdminLayout>
        <div className="rounded-xl border border-dashed border-surface-200 bg-white px-6 py-16 text-center">
          <p className="text-sm text-ink-500">جاري تحميل الفرصة…</p>
        </div>
      </AdminLayout>
    );
  }

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const updateListItem = (key, i, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].map((item, idx) => (idx === i ? value : item)),
    }));

  const addListItem = (key, emptyValue) =>
    setForm((f) => ({ ...f, [key]: [...f[key], emptyValue] }));

  const removeListItem = (key, i) =>
    setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  const buildPayload = () => {
    const clean = (arr) => arr.filter((s) => s && String(s).trim().length > 0);
    return {
      title: form.title.trim(),
      summary: form.summary.trim(),
      executiveSummary: (form.executiveSummary || "").trim(),
      score: Number(form.score) || 0,
      type: form.type,
      country: form.country,
      industry: form.industry,
      highlights: clean(form.highlights),
      tags: clean(form.tags),
      keyIndicators: form.keyIndicators
        .filter((k) => k.label?.trim() || k.value?.trim())
        .map((k) => ({ label: k.label?.trim() || "", value: k.value?.trim() || "" })),
      suggestedSteps: clean(form.suggestedSteps),
    };
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await adminPatchOpportunity(base.id, buildPayload());
      toast.success("تم حفظ التعديلات");
      refreshPublic();
    } catch (e) {
      toast.error("تعذّر حفظ التعديلات", { description: String(e) });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (isPublishing) return;
    setIsPublishing(true);
    try {
      // Save latest edits first, then publish — one atomic UX action.
      await adminPatchOpportunity(base.id, buildPayload());
      await adminPublishOpportunity(base.id);
      toast.success("تم نشر الفرصة بنجاح");
      refreshPublic();
      navigate("/admin/review?status=published");
    } catch (e) {
      toast.error("تعذّر نشر الفرصة", { description: String(e) });
      setIsPublishing(false);
    }
  };

  return (
    <AdminLayout>
      <section data-testid={ADMIN.editorRoot} className="space-y-6">
        {/* Breadcrumb + heading */}
        <div className="space-y-2">
          <Link
            to="/admin/review"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-500 transition-colors hover:text-ink-900"
          >
            <ArrowRight className="h-3.5 w-3.5 -scale-x-100" strokeWidth={1.75} />
            العودة إلى المراجعة
          </Link>
          <h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
            محرِّر الفرصة
          </h2>
          <p className="text-xs text-ink-500">
            المعرِّف: <span className="font-mono">{base.id}</span>
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          className="space-y-6"
        >
          {/* Basics */}
          <div className="rounded-xl border border-surface-200 bg-white p-5 sm:p-6">
            <h3 className="text-sm font-semibold text-ink-900">البيانات الأساسية</h3>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="العنوان" required span={2}>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  className={baseInput}
                  data-testid={ADMIN.editorField("title")}
                />
              </Field>
              <Field label="النوع" required>
                <select
                  value={form.type}
                  onChange={(e) => update("type", e.target.value)}
                  className={baseInput}
                  data-testid={ADMIN.editorField("type")}
                >
                  {opportunityTypes.map((c) => (
                    <option key={c.slug} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="الدولة" required>
                <select
                  value={form.country}
                  onChange={(e) => update("country", e.target.value)}
                  className={baseInput}
                  data-testid={ADMIN.editorField("country")}
                >
                  {countries.map((c) => (
                    <option key={c.slug} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="القطاع" required>
                <select
                  value={form.industry}
                  onChange={(e) => update("industry", e.target.value)}
                  className={baseInput}
                  data-testid={ADMIN.editorField("industry")}
                >
                  {industries.map((i) => (
                    <option key={i.slug} value={i.name}>{i.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="النقاط" hint="من 0 إلى 100" required>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={form.score}
                  onChange={(e) => update("score", Number(e.target.value))}
                  className={`${baseInput} font-mono`}
                  data-testid={ADMIN.editorField("score")}
                />
              </Field>
              <Field label="الملخّص العام" hint="حتى 240 حرفاً" required span={2}>
                <textarea
                  value={form.summary}
                  onChange={(e) => update("summary", e.target.value)}
                  rows={3}
                  className={baseInput}
                  data-testid={ADMIN.editorField("summary")}
                />
              </Field>
            </div>
          </div>

          {/* Executive summary */}
          <div className="rounded-xl border border-surface-200 bg-white p-5 sm:p-6">
            <h3 className="text-sm font-semibold text-ink-900">الملخّص التنفيذي</h3>
            <Field label="الفقرة التنفيذية" hint="عرض تفصيلي على صفحة الفرصة" span={2}>
              <textarea
                value={form.executiveSummary}
                onChange={(e) => update("executiveSummary", e.target.value)}
                rows={5}
                className={`${baseInput} mt-4`}
                data-testid={ADMIN.editorField("executiveSummary")}
              />
            </Field>
          </div>

          {/* Highlights */}
          <RepeatingList
            title="أبرز النقاط"
            items={form.highlights}
            testIdBase={ADMIN.editorField("highlights")}
            onChange={(i, v) => updateListItem("highlights", i, v)}
            onAdd={() => addListItem("highlights", "")}
            onRemove={(i) => removeListItem("highlights", i)}
            placeholder="اكتب نقطة موجزة…"
          />

          {/* Key indicators */}
          <div className="rounded-xl border border-surface-200 bg-white p-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-ink-900">المؤشِّرات الرئيسية</h3>
              <button
                type="button"
                onClick={() => addListItem("keyIndicators", { label: "", value: "" })}
                className="inline-flex items-center gap-1 rounded-md border border-surface-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 transition-colors hover:border-intel-500 hover:text-intel-800"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={1.75} />
                إضافة مؤشر
              </button>
            </div>
            <div className="mt-4 space-y-2">
              {form.keyIndicators.map((ind, i) => (
                <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]">
                  <input
                    type="text"
                    value={ind.label}
                    placeholder="الاسم — مثلاً: الإيرادات السنوية"
                    onChange={(e) =>
                      updateListItem("keyIndicators", i, { ...ind, label: e.target.value })
                    }
                    className={baseInput}
                    data-testid={ADMIN.editorField(`indicator-label-${i}`)}
                  />
                  <input
                    type="text"
                    value={ind.value}
                    placeholder="القيمة — مثلاً: 24 مليون ريال"
                    onChange={(e) =>
                      updateListItem("keyIndicators", i, { ...ind, value: e.target.value })
                    }
                    className={baseInput}
                    data-testid={ADMIN.editorField(`indicator-value-${i}`)}
                  />
                  <button
                    type="button"
                    onClick={() => removeListItem("keyIndicators", i)}
                    className="inline-flex h-9 items-center justify-center rounded-md border border-surface-200 px-2 text-red-600 transition-colors hover:bg-red-50"
                    aria-label="حذف المؤشر"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Suggested steps */}
          <RepeatingList
            title="الخطوات المقترحة"
            items={form.suggestedSteps}
            testIdBase={ADMIN.editorField("step")}
            onChange={(i, v) => updateListItem("suggestedSteps", i, v)}
            onAdd={() => addListItem("suggestedSteps", "")}
            onRemove={(i) => removeListItem("suggestedSteps", i)}
            placeholder="اكتب خطوة…"
            numbered
          />

          {/* Tags */}
          <div className="rounded-xl border border-surface-200 bg-white p-5 sm:p-6">
            <h3 className="text-sm font-semibold text-ink-900">الوسوم</h3>
            <Field label="الوسوم" hint="افصل بين الوسوم بفاصلة" span={2}>
              <input
                type="text"
                value={form.tags.join("، ")}
                onChange={(e) =>
                  update(
                    "tags",
                    e.target.value
                      .split(/[،,]/)
                      .map((t) => t.trim())
                      .filter(Boolean),
                  )
                }
                className={`${baseInput} mt-4`}
                data-testid={ADMIN.editorField("tags")}
              />
            </Field>
          </div>

          {/* Actions */}
          <div className="sticky bottom-4 flex items-center justify-end gap-2 rounded-xl border border-surface-200 bg-white/95 p-3 shadow-sm backdrop-blur">
            <Link
              to="/admin/review"
              className="inline-flex h-10 items-center rounded-md border border-surface-200 bg-white px-4 text-sm font-medium text-ink-700 transition-colors hover:bg-surface-50"
            >
              إلغاء
            </Link>
            <button
              type="submit"
              disabled={isSaving || isPublishing}
              data-testid={ADMIN.editorSave}
              className="inline-flex h-10 items-center gap-1.5 rounded-md border border-surface-200 bg-white px-4 text-sm font-medium text-ink-900 transition-colors hover:bg-surface-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSaving
                ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                : <Save className="h-4 w-4" strokeWidth={1.75} />}
              {isSaving ? "جاري الحفظ…" : "حفظ التعديلات"}
            </button>
            <button
              type="button"
              onClick={handlePublish}
              disabled={isSaving || isPublishing}
              data-testid={ADMIN.editorPublish}
              className="inline-flex h-10 items-center gap-1.5 rounded-md bg-intel-800 px-4 text-sm font-medium text-white transition-colors hover:bg-intel-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isPublishing
                ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                : <CheckCircle2 className="h-4 w-4" strokeWidth={1.75} />}
              {isPublishing ? "جاري النشر…" : "نشر"}
            </button>
          </div>
        </form>
      </section>
    </AdminLayout>
  );
}

/** Reusable repeating string list (highlights / steps). */
function RepeatingList({ title, items, testIdBase, onChange, onAdd, onRemove, placeholder, numbered }) {
  return (
    <div className="rounded-xl border border-surface-200 bg-white p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink-900">{title}</h3>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1 rounded-md border border-surface-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 transition-colors hover:border-intel-500 hover:text-intel-800"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={1.75} />
          إضافة
        </button>
      </div>
      <div className="mt-4 space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            {numbered && (
              <span className="mt-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-intel-50 font-mono text-xs font-semibold text-intel-800">
                {i + 1}
              </span>
            )}
            <input
              type="text"
              value={item}
              placeholder={placeholder}
              onChange={(e) => onChange(i, e.target.value)}
              data-testid={`${testIdBase}-${i}`}
              className="block w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-ink-900 shadow-sm outline-none transition-colors placeholder:text-ink-500/70 focus:border-intel-500 focus:ring-2 focus:ring-intel-500/20"
            />
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="inline-flex h-9 items-center justify-center rounded-md border border-surface-200 px-2 text-red-600 transition-colors hover:bg-red-50"
              aria-label="حذف"
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
